import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getSEO, buildCanonical } from "../../config/seoConfig";
import { cmsPageMeta } from "../../cms/bundle";

/**
 * SEOManager
 *
 * Imperatively keeps <title>, <meta name="description">, <link rel="canonical">,
 * and core og/twitter tags in sync with the current route. Runs on every SPA
 * route change so the static index.html defaults (homepage canonical, default
 * title) cannot bleed into other pages.
 *
 * Pages that set their own title/description/canonical (blog post, state ESA
 * page, etc.) will still win, because those pages mount AFTER this runs and
 * overwrite the same tags. SEOManager guarantees a correct fallback and — on
 * pages with no explicit meta — provides the centralized doc-sourced values.
 *
 * Also always forces the canonical to be self-referencing for each route,
 * fixing the bug where every page previously canonicalized to the homepage.
 */

const PAGE_TITLE_FALLBACK = "PawTenant — Legitimate ESA Letters";
const PAGE_DESC_FALLBACK =
  "Get a legitimate ESA letter from licensed mental health professionals. Valid in all US states.";

/**
 * PAWTENANT-BING-INDEXING-HYGIENE-001.
 *
 * The server-rendered <head>, captured once when this module loads — before
 * React or any page component touches it. scripts/prerender-seo.mjs writes a
 * self-referencing canonical plus the route's own title and description into
 * every prerendered route, including the ~115 data-driven blog posts and the 51
 * /blog/state/* pages that have NO entry in seoConfig.ts. Without this, the
 * effect below replaced their unique description with the generic fallback on
 * every page load, so a JavaScript-rendering crawler (Bing) saw 115 identical
 * 94-character descriptions.
 *
 * It is trusted ONLY for the route it was rendered for: its canonical must equal
 * the canonical of the route being processed. The fallback app shell carries no
 * canonical, and after a client-side navigation the canonical no longer
 * matches, so neither case can borrow another route's text.
 */
const SERVER_HEAD: { canonical: string | null; title: string; description: string } | null =
  typeof document === "undefined"
    ? null
    : {
        canonical:
          document.head.querySelector('link[rel="canonical"]')?.getAttribute("href") ?? null,
        title: document.title,
        description:
          document.head.querySelector('meta[name="description"]')?.getAttribute("content") ?? "",
      };

function setMetaByName(name: string, content: string) {
  let el = document.head.querySelector(
    `meta[name="${name}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) {
    el.setAttribute("content", content);
  }
}

function setMetaByProperty(property: string, content: string) {
  let el = document.head.querySelector(
    `meta[property="${property}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) {
    el.setAttribute("content", content);
  }
}

function setCanonical(href: string) {
  let el = document.head.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  if (el.getAttribute("href") !== href) {
    el.setAttribute("href", href);
  }
}

function setOgUrl(href: string) {
  setMetaByProperty("og:url", href);
}

export default function SEOManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const entry = getSEO(pathname);
    const canonical = buildCanonical(pathname);

    // No central entry: keep the head the server rendered for THIS route, if it
    // was rendered for this route, instead of the generic fallback.
    const server =
      !entry && SERVER_HEAD && SERVER_HEAD.canonical === canonical ? SERVER_HEAD : null;

    const compiledTitle = entry?.title ?? (server?.title || PAGE_TITLE_FALLBACK);
    const description = entry?.description ?? (server?.description || PAGE_DESC_FALLBACK);

    // PAWTENANT-CONTENT-STUDIO-INTEGRATION-001: a metadata override published
    // through Content Studio (frozen into src/generated/cmsBundle.json at build
    // time) wins over seoConfig and the server head, on every client-side
    // navigation. scripts/prerender-seo.mjs applies the same override to the
    // raw <head>, so crawlers and the browser agree. The canonical never changes.
    const cms = cmsPageMeta(pathname);
    const title = cms?.title ?? compiledTitle;
    const effectiveDescription = cms?.description ?? description;

    if (document.title !== title) {
      document.title = title;
    }

    setMetaByName("description", effectiveDescription);
    setCanonical(canonical);

    setMetaByProperty("og:title", cms?.og_title ?? title);
    setMetaByProperty("og:description", cms?.og_description ?? effectiveDescription);
    setOgUrl(canonical);
    if (cms?.og_image) setMetaByProperty("og:image", cms.og_image);

    setMetaByName("twitter:title", cms?.og_title ?? title);
    setMetaByName("twitter:description", cms?.og_description ?? effectiveDescription);
  }, [pathname]);

  return null;
}
